import type { DashboardDto, MeResponse } from '@process-hub/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { SortHeader, useSortedRows, type SortColumn } from '../components/SortableTable';
import { describeError } from '../errors';
import { formatWhen } from '../time';

type DepartmentRow = DashboardDto['byDepartment'][number];

/** Sortable columns of the department table (D-047); counts and shares show the biggest first. */
const DEPARTMENT_COLUMNS: ReadonlyArray<SortColumn<DepartmentRow>> = [
  { key: 'department', get: (r) => r.department },
  { key: 'published', get: (r) => r.published, firstDir: 'desc' },
  { key: 'agentShare', get: (r) => r.agentShare, firstDir: 'desc' },
];

/** The four numbers from the prototype, per department shares, and what happened recently. */
export function Dashboard({ me }: { me: MeResponse }) {
  const [d, setD] = useState<DashboardDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const byDepartment = useSortedRows(d?.byDepartment, DEPARTMENT_COLUMNS, { key: 'department', dir: 'asc' });

  useEffect(() => {
    api
      .dashboard()
      .then(setD)
      .catch((e: unknown) => setError(describeError(e)));
  }, []);

  if (error) return <div className="notice notice--error">{error}</div>;
  if (!d) return <div className="notice">Loading.</div>;

  const pct = (x: number | null) => (x === null ? '—' : `${Math.round(x * 100)}%`);
  const scope = me.isAdmin ? 'all departments' : me.departments.map((x) => x.name).join(', ') || 'no department';

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="kpis">
        <Kpi label="Published processes" value={d.published} sub={scope} accent="blue" to="/" />
        <Kpi label="Awaiting approval" value={d.inApproval} sub={d.awaitingMe ? `${d.awaitingMe} for you` : 'none for you'} accent="orange" to="/approvals" />
        <Kpi label="Stale or orphaned" value={d.staleOrOrphaned} sub={`${d.dueForRecert} due for re-certification within 30 days`} accent="gold" to="/?stale=1" />
        <Kpi label="Steps performed by agents" value={pct(d.agentShare)} sub={`hybrid ${pct(d.hybridShare)} · published versions only`} accent="light" to="/automation" />
      </div>

      <div className="dashboard__grid">
        <div className="card">
          <div className="card__header">
            <div className="card__title">By department</div>
          </div>
          {d.byDepartment.length === 0 ? (
            <div className="notice" style={{ margin: 18 }}>
              No published processes yet.
            </div>
          ) : (
            <table className="grid">
              <thead>
                <tr>
                  <SortHeader label="Department" sortKey="department" sort={byDepartment.sort} onToggle={byDepartment.toggle} />
                  <SortHeader label="Published" sortKey="published" sort={byDepartment.sort} onToggle={byDepartment.toggle} />
                  <SortHeader label="Agent steps" sortKey="agentShare" sort={byDepartment.sort} onToggle={byDepartment.toggle} />
                </tr>
              </thead>
              <tbody>
                {byDepartment.sorted.map((r) => (
                  <tr key={r.department}>
                    <td className="grid__primary">{r.department}</td>
                    <td className="mono">{r.published}</td>
                    <td className="mono">{pct(r.agentShare)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card">
          <div className="card__header">
            <div className="card__title">Recent activity</div>
          </div>
          {d.recentEvents.length === 0 ? (
            <div className="notice" style={{ margin: 18 }}>
              Nothing yet.
            </div>
          ) : (
            <ul className="activity">
              {d.recentEvents.map((e, i) => (
                <li key={i}>
                  <span className="mono muted">{formatWhen(e.occurredAt)}</span>
                  <span>
                    <strong>{describeEvent(e.eventType)}</strong>
                    {e.processName && (
                      <>
                        {' · '}
                        {e.slug ? <Link to={`/processes/${e.slug}`}>{e.processName}</Link> : e.processName}
                      </>
                    )}
                    <span className="muted"> — {e.actorName}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, accent, to }: { label: string; value: number | string; sub: string; accent: 'blue' | 'orange' | 'gold' | 'light'; to: string }) {
  return (
    <Link to={to} className={`kpi kpi--${accent}`}>
      <div className="kpi__label">{label}</div>
      <div className="kpi__value">{value}</div>
      <div className="kpi__sub">{sub}</div>
    </Link>
  );
}

export const EVENT_LABEL: Record<string, string> = {
  VersionCreated: 'Process created',
  DraftUpdated: 'Draft edited',
  InterviewProposal: 'Interview updated the record',
  RevisionOpened: 'Revision opened',
  SubmittedForApproval: 'Submitted for approval',
  StageApproved: 'Stage approved',
  Approved: 'Approved and published',
  ChangesRequested: 'Changes requested',
  Rejected: 'Rejected',
  Flagged: 'Flagged by a reader',
  FlagResolved: 'Flag resolved',
  RecertReminder: 'Re-certification due soon',
  MarkedStale: 'Marked stale',
  MarkedOrphaned: 'Marked orphaned',
  SystemDecommissioned: 'Referenced system decommissioned',
  Recertified: 'Re-certified',
  TeamsWebhookChanged: 'Teams channel changed',
  OwnerReassigned: 'Owner reassigned',
  Retired: 'Retired',
  Restored: 'Restored from retirement',
  RolesChanged: 'Roles changed',
  ScreenshotsAdded: 'Screenshots added',
  ScreenshotsAssigned: 'Screenshots placed on steps',
  ScreenshotMoved: 'Screenshot moved',
  ScreenshotRemoved: 'Screenshot removed',
  InterviewFinished: 'Interview finished',
  TeamsTestSent: 'Teams test card sent',
  ProcessDeleted: 'Draft deleted',
  InterviewResumed: 'Interview resumed',
  AutomationProposed: 'Automation proposed',
  AutomationAccepted: 'Automation accepted',
  AutomationDeclined: 'Automation declined',
  AutomationShipped: 'Agent live on a step',
  AutomationWithdrawn: 'Automation proposal withdrawn',
};
export const describeEvent = (t: string): string => EVENT_LABEL[t] ?? t;
